import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '@/stores';
import { useRooms } from '@/entities/room';
import { useEstimate } from '@/features/estimate';
import { useDatabase } from '@/entities/service';
import { Service, Recipe } from '@/shared/model';

import { RecipeSelectorModal } from '@/features/database/ui/modals/RecipeSelectorModal';
import { RecipeEditorModal } from '@/features/database/ui/modals/RecipeEditorModal';
import { RecipePreviewModal } from '@/features/database/ui/modals/RecipePreviewModal';
import { ConfirmationModal } from '@/shared/ui/ConfirmationModal';

export const RecipeModals: FC = () => {
  const { t } = useTranslation();
  const activeModal = useUIStore((s) => s.activeModal);
  const modalData = useUIStore((s) => s.modalData);
  const closeModal = useUIStore((s) => s.closeModal);
  const openModal = useUIStore((s) => s.openModal);
  const { addServicesToRoom, addServicesToProject } = useRooms();
  const { activeRoom } = useEstimate();
  const { 
    services, recipes, categories, units, createNewRecipe, deleteRecipe,
    updateRecipe
  } = useDatabase();

  return (
    <>
      {activeModal === 'recipe_selector' && (
        <RecipeSelectorModal
          isOpen={true}
          onClose={closeModal}
          recipes={recipes}
          onSelect={(recipe) => {
              openModal('recipe_preview', { recipe, target: modalData?.target });
          }}
          onCreate={(cat) => {
              const newRecipe = createNewRecipe(cat);
              if (newRecipe) openModal('recipe_editor', { 
                id: newRecipe.id, 
                category: cat, 
                initialRecipe: newRecipe,
                returnTo: 'recipe_selector'
              });
          }}
          onEdit={(recipe, cat) => openModal('recipe_editor', { 
            id: recipe.id, 
            category: cat,
            returnTo: 'recipe_selector'
          })}
          onDelete={(id, cat) => openModal('recipe_delete_confirm', { id, category: cat, returnTo: 'recipe_selector' })}
        />
      )}

      {activeModal === 'recipe_preview' && modalData?.recipe && (
        <RecipePreviewModal
          isOpen={true}
          recipe={modalData.recipe as Recipe}
          servicesDB={services}
          onClose={() => openModal('recipe_selector')}
          onApply={(recipe) => {
              const servicesToAdd: Service[] = [];
              recipe.layers.forEach(l => {
                  const s = services.find(srv => srv.id === l.serviceId);
                  if (s) {
                      servicesToAdd.push({ ...s, adjustment: l.coefficient });
                  }
              });

              if (modalData?.target === 'global') {
                  addServicesToProject(servicesToAdd);
                  closeModal();
              } else if (activeRoom) {
                  addServicesToRoom(activeRoom.id, servicesToAdd);
                  closeModal();
              }
          }}
        />
      )}

      {activeModal === 'recipe_editor' && modalData?.id && (
        (() => {
          const id = modalData.id as string;
          const category = modalData.category as string;
          const initialRecipe = modalData.initialRecipe as Recipe | undefined;
          const returnTo = modalData.returnTo as string | undefined;
          const currentRecipe = (recipes[category] || []).find((r: Recipe) => r.id === id) || initialRecipe || null;
          
          if (!currentRecipe) return null;

          return (
            <RecipeEditorModal 
              key={currentRecipe.id}
              isOpen={true}
              onClose={() => {
                if (returnTo === 'recipe_selector') {
                  openModal('recipe_selector');
                } else {
                  closeModal();
                }
              }}
              recipe={currentRecipe}
              category={category}
              services={services}
              categories={categories}
              units={units}
              onSave={(updatedRecipe) => {
                  updateRecipe(category as string, id as string, { 
                    name: updatedRecipe.name, 
                    layers: updatedRecipe.layers 
                  });
              }}
            />
          );
        })()
      )}

      {activeModal === 'recipe_delete_confirm' && (
        <ConfirmationModal 
          isOpen={true}
          onClose={() => {
            if (modalData?.returnTo === 'recipe_selector') {
              openModal('recipe_selector');
            } else {
              closeModal();
            }
          }}
          onConfirm={() => { 
            if(modalData?.id && modalData?.category) {
              deleteRecipe(modalData.category as string, modalData.id as string); 
            }
            if (modalData?.returnTo === 'recipe_selector') {
              openModal('recipe_selector');
            } else {
              closeModal();
            }
          }}
          title={t('editor.modals.delete_recipe_title', 'Удаление пакета')}
          message={t('editor.modals.delete_recipe_message', 'Удалить этот пакет услуг?')}
        />
      )}
    </>
  );
};